import { useCallback, useState } from "react";
import { KEYS, load, save, remove } from "@/lib/storage";

const DEFAULTS = {
  totals: { played: 0, wins: 0, losses: 0, draws: 0 },
  byRuleset: {}, // { [rulesetId]: { played, wins, losses, draws } }
  byDifficulty: {}, // { beginner: { played, wins, losses, draws }, ... }
  avgMoves: 0,
  avgCapturesPerGame: 0,
  largestCapture: 0,
  currentStreak: 0,
  longestStreak: 0,
  tutorialCompleted: false,
  totalPlayTimeMs: 0,
};

export function getStats() {
  return { ...DEFAULTS, ...(load(KEYS.stats) || {}) };
}

function mergeBucket(b, outcome) {
  const cur = b || { played: 0, wins: 0, losses: 0, draws: 0 };
  cur.played += 1;
  if (outcome === "win") cur.wins += 1;
  else if (outcome === "loss") cur.losses += 1;
  else if (outcome === "draw") cur.draws += 1;
  return cur;
}

export function recordGame({ rulesetId, difficulty, outcome, moves, captures, largestCapture, durationMs }) {
  const s = getStats();
  s.totals = mergeBucket(s.totals, outcome);
  s.byRuleset[rulesetId] = mergeBucket(s.byRuleset[rulesetId], outcome);
  if (difficulty) s.byDifficulty[difficulty] = mergeBucket(s.byDifficulty[difficulty], outcome);

  // Running averages
  const n = s.totals.played;
  s.avgMoves = ((s.avgMoves * (n - 1)) + (moves || 0)) / n;
  s.avgCapturesPerGame = ((s.avgCapturesPerGame * (n - 1)) + (captures || 0)) / n;
  if ((largestCapture || 0) > s.largestCapture) s.largestCapture = largestCapture;
  s.totalPlayTimeMs = (s.totalPlayTimeMs || 0) + (durationMs || 0);

  if (outcome === "win") {
    s.currentStreak = (s.currentStreak || 0) + 1;
    if (s.currentStreak > s.longestStreak) s.longestStreak = s.currentStreak;
  } else if (outcome === "loss") {
    s.currentStreak = 0;
  }
  save(KEYS.stats, s);
  return s;
}

export function markTutorialCompleted() {
  const s = getStats();
  s.tutorialCompleted = true;
  save(KEYS.stats, s);
}

export function resetStats() {
  remove(KEYS.stats);
}

export function useStats() {
  const [stats, setStats] = useState(() => getStats());
  const refresh = useCallback(() => setStats(getStats()), []);
  const reset = useCallback(() => {
    resetStats();
    setStats(getStats());
  }, []);
  return { stats, refresh, reset };
}
